import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Home, Plus, Trash2, User, Users, RefreshCw, UserPlus, Check, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const frequencies = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'as_needed', label: 'As Needed' },
];

const HouseholdTasksPage = () => {
  const { family, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({
    task: '',
    owner: '',
    backup: '',
    frequency: 'weekly',
    notes: ''
  });
  const [familyRoles, setFamilyRoles] = useState(null);
  
  // E7: AI Rebalance state
  const [aiRebalancing, setAiRebalancing] = useState(false);
  const [rebalanceSuggestions, setRebalanceSuggestions] = useState(null);

  useEffect(() => {
    fetchTasks();
    fetchFamilyRoles();
  }, []);

  const fetchFamilyRoles = async () => {
    try {
      const response = await axios.get(`${API}/family/roles`);
      setFamilyRoles(response.data);
    } catch (error) {
      console.error('Error fetching family roles:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/household-tasks`);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/household-tasks`, newTask);
      toast.success('Task added!');
      setDialogOpen(false);
      setNewTask({ task: '', owner: '', backup: '', frequency: 'weekly', notes: '' });
      fetchTasks();
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const deleteTask = async (id) => {
    try {
      await axios.delete(`${API}/household-tasks/${id}`);
      toast.success('Task removed');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  // Bug 3 Fix: Assign owner to task - using PATCH endpoint
  const assignOwner = async (task, newOwner) => {
    try {
      await axios.patch(`${API}/household-tasks/${task.id}`, {
        owner: newOwner
      });
      toast.success(`Assigned to ${newOwner}`);
      fetchTasks();
    } catch (error) {
      console.error('Assign error:', error.response?.data || error.message);
      toast.error('Failed to assign task');
    }
  };

  // Bug 4 Fix: Mark task as done
  const markTaskDone = async (task) => {
    try {
      await axios.patch(`${API}/household-tasks/${task.id}`, {
        completed_at: new Date().toISOString()
      });
      toast.success('Task marked as done!');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to mark task done');
    }
  };

  // E7: AI Rebalance tasks
  const handleAIRebalance = async () => {
    if (tasks.length === 0) {
      toast.info('Add some tasks first to get rebalancing suggestions');
      return;
    }
    
    setAiRebalancing(true);
    try {
      const response = await axios.post(`${API}/ai/household-tasks/rebalance`, {
        current_tasks: tasks,
        family_members: familyRoles?.members || []
      });
      setRebalanceSuggestions(response.data.suggestions);
      toast.success('AI suggestions ready!');
    } catch (error) {
      toast.error('Failed to get AI suggestions');
    } finally {
      setAiRebalancing(false);
    }
  };

  // Bug 2 Fix: Get family members from /api/family/roles (not family.members)
  const familyMembers = familyRoles?.members?.map(m => m.name) || [];
  // If no members yet, at least include the current user
  const assignableMembers = familyMembers.length > 0 ? familyMembers : [user?.name].filter(Boolean);

  const groupedByOwner = tasks.reduce((acc, task) => {
    const owner = task.owner || 'Unassigned';
    if (!acc[owner]) acc[owner] = [];
    acc[owner].push(task);
    return acc;
  }, {});

  const getFrequencyColor = (freq) => {
    switch (freq) {
      case 'daily': return 'bg-red-100 text-red-700';
      case 'weekly': return 'bg-blue-100 text-blue-700';
      case 'monthly': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="household-tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <Home className="w-5 h-5 text-teal-600" />
            </div>
            Household Tasks
          </h1>
          <p className="text-nurture-muted mt-1">Assign clear ownership for household responsibilities</p>
        </div>

        <div className="flex gap-2">
          {/* E7: AI Rebalance Button */}
          <Button
            variant="outline"
            onClick={handleAIRebalance}
            disabled={aiRebalancing || tasks.length === 0}
            className="rounded-full border-nurture-terracotta text-nurture-terracotta hover:bg-nurture-terracotta/10"
            data-testid="ai-rebalance-btn"
          >
            {aiRebalancing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {aiRebalancing ? 'Analyzing...' : 'AI Rebalance'}
          </Button>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover text-white rounded-full shadow-nurture-button" data-testid="add-household-task-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Task
              </Button>
            </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-heading">Add Household Task</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Task</Label>
                <Input
                  value={newTask.task}
                  onChange={(e) => setNewTask({ ...newTask, task: e.target.value })}
                  placeholder="Laundry"
                  required
                  className="rounded-xl"
                  data-testid="household-task-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Owner (Primary)</Label>
                  <Input
                    value={newTask.owner}
                    onChange={(e) => setNewTask({ ...newTask, owner: e.target.value })}
                    placeholder="Mom"
                    required
                    className="rounded-xl"
                    list="family-members"
                    data-testid="household-owner-input"
                  />
                  <datalist id="family-members">
                    {familyMembers.map(m => <option key={m} value={m} />)}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label>Backup</Label>
                  <Input
                    value={newTask.backup}
                    onChange={(e) => setNewTask({ ...newTask, backup: e.target.value })}
                    placeholder="Dad"
                    className="rounded-xl"
                    list="family-members-backup"
                    data-testid="household-backup-input"
                  />
                  <datalist id="family-members-backup">
                    {familyMembers.map(m => <option key={m} value={m} />)}
                  </datalist>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select value={newTask.frequency} onValueChange={(v) => setNewTask({ ...newTask, frequency: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="household-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencies.map(freq => (
                      <SelectItem key={freq.value} value={freq.value}>{freq.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={newTask.notes}
                  onChange={(e) => setNewTask({ ...newTask, notes: e.target.value })}
                  placeholder="Any specific instructions..."
                  className="rounded-xl"
                  data-testid="household-notes"
                />
              </div>
              <Button type="submit" className="w-full bg-nurture-terracotta hover:bg-nurture-terracotta-hover rounded-full" data-testid="household-submit">
                Add Task
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* E7: AI Rebalance Suggestions Card */}
      {rebalanceSuggestions && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="rounded-2xl border-2 border-nurture-terracotta/30 bg-orange-50/50 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-nurture-navy">
                <span className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-nurture-terracotta" />
                  AI Rebalancing Suggestions
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setRebalanceSuggestions(null)}
                  className="text-nurture-muted"
                >
                  Dismiss
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {rebalanceSuggestions.analysis && (
                <p className="text-sm text-nurture-muted">{rebalanceSuggestions.analysis}</p>
              )}
              
              {rebalanceSuggestions.suggestions?.map((suggestion, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-xl border border-nurture-cream-dark">
                  <div>
                    <p className="font-medium text-nurture-navy">{suggestion.task}</p>
                    <p className="text-sm text-nurture-muted">
                      {suggestion.current_owner} → <span className="text-nurture-terracotta font-medium">{suggestion.suggested_owner}</span>
                    </p>
                    <p className="text-xs text-nurture-muted mt-1">{suggestion.reason}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      const task = tasks.find(t => t.task === suggestion.task);
                      if (task) {
                        await assignOwner(task, suggestion.suggested_owner);
                      }
                    }}
                    className="rounded-xl text-nurture-terracotta border-nurture-terracotta"
                  >
                    Apply
                  </Button>
                </div>
              ))}
              
              {rebalanceSuggestions.conversation_starter && (
                <div className="p-3 bg-nurture-cream rounded-xl">
                  <p className="text-sm font-medium text-nurture-navy mb-1">Conversation starter:</p>
                  <p className="text-sm text-nurture-muted italic">"{rebalanceSuggestions.conversation_starter}"</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Tasks by Owner */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-48 bg-nurture-cream-dark rounded-3xl animate-pulse" />
          ))}
        </div>
      ) : Object.keys(groupedByOwner).length === 0 ? (
        <Card className="rounded-3xl shadow-nurture">
          <CardContent className="py-16 text-center">
            <Home className="w-16 h-16 mx-auto text-nurture-muted/30 mb-4" />
            <h3 className="text-xl font-heading font-semibold text-nurture-navy mb-2">No tasks assigned yet</h3>
            <p className="text-nurture-muted mb-4">Assign clear ownership for household responsibilities</p>
            <p className="text-sm text-nurture-muted max-w-md mx-auto">
              This helps reduce the "default parent" problem by making responsibilities visible and explicit.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(groupedByOwner).map(([owner, ownerTasks]) => (
            <motion.div
              key={owner}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="rounded-3xl shadow-nurture h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-nurture-navy flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-nurture-terracotta/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-nurture-terracotta" />
                    </div>
                    {owner}
                    <Badge variant="outline" className="ml-auto">{ownerTasks.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <AnimatePresence>
                      {ownerTasks.map((task) => (
                        <motion.div
                          key={task.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 10 }}
                          className="p-3 rounded-xl bg-nurture-cream/50 group"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className={`font-medium ${task.completed_at ? 'text-nurture-muted line-through' : 'text-nurture-navy'}`}>{task.task}</p>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                {task.frequency && (
                                  <Badge className={`text-xs ${getFrequencyColor(task.frequency)}`}>
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    {task.frequency}
                                  </Badge>
                                )}
                                {task.backup && (
                                  <Badge variant="outline" className="text-xs">
                                    <Users className="w-3 h-3 mr-1" />
                                    Backup: {task.backup}
                                  </Badge>
                                )}
                                {task.completed_at && (
                                  <Badge className="text-xs bg-green-100 text-green-700">
                                    <Check className="w-3 h-3 mr-1" />
                                    Done
                                  </Badge>
                                )}
                                {/* Bug 3 Fix: Assign owner dropdown - show for unassigned, use assignableMembers */}
                                {(owner === 'Unassigned' || !task.owner || task.owner === 'Unassigned') && assignableMembers.length > 0 && !task.completed_at && (
                                  <Select onValueChange={(val) => assignOwner(task, val)}>
                                    <SelectTrigger className="h-7 w-auto text-xs border-dashed">
                                      <UserPlus className="w-3 h-3 mr-1" />
                                      <span>Assign</span>
                                    </SelectTrigger>
                                    <SelectContent>
                                      {assignableMembers.map((member) => (
                                        <SelectItem key={member} value={member}>{member}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              </div>
                              {task.notes && (
                                <p className="text-xs text-nurture-muted mt-2">{task.notes}</p>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {/* Bug 4 Fix: Mark Done button with checkmark icon */}
                              {!task.completed_at && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => markTaskDone(task)}
                                  className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  title="Mark as done"
                                  data-testid={`mark-done-${task.id}`}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteTask(task.id)}
                                className="h-7 w-7 text-nurture-muted hover:text-red-500"
                                data-testid={`delete-household-${task.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HouseholdTasksPage;
